(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (root && !root.__reviliaCartInitialized) {
    root.__reviliaCartInitialized = true;
    api.init(root);
  }
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';

  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const TEXT = {
    changed: 'Le panier a changé. Vérifiez les quantités mises à jour avant de réessayer.',
    failed: 'La modification n’a pas pu être confirmée. Vérifiez le panier mis à jour avant de réessayer.',
    stock: 'La quantité demandée n’est pas disponible. Vérifiez les quantités mises à jour.',
    unknown: 'Impossible de vérifier le panier. Vérifiez-le à nouveau avant de continuer le paiement.',
    invalid: 'Les quantités de cette offre ne correspondent plus à sa configuration. Supprimez cette offre puis ajoutez-la à nouveau depuis la page du produit.',
    unsupported: 'Pour modifier cette offre, utilisez la page du produit.',
    pending: 'Mise à jour du panier en cours…',
    timeout: 'La mise à jour prend plus de temps que prévu. Le paiement reste bloqué jusqu’à la vérification du panier.'
  };

  function stable(value) {
    if (Array.isArray(value)) return '[' + value.map(stable).join(',') + ']';
    if (value && typeof value === 'object') {
      return '{' + Object.keys(value).sort().map(key => JSON.stringify(key) + ':' + stable(value[key])).join(',') + '}';
    }
    return JSON.stringify(value);
  }

  function propertiesObject(value) {
    if (!value) return {};
    if (Array.isArray(value)) {
      if (value.some(pair => !Array.isArray(pair) || pair.length !== 2)) throw new Error('Invalid line properties');
      return Object.fromEntries(value);
    }
    if (typeof value !== 'object') throw new Error('Invalid line properties');
    return { ...value };
  }

  function decodeLiquidProperty(value) {
    if (typeof value !== 'string') return value;
    const named = { amp: '&', quot: '"', apos: "'", lt: '<', gt: '>' };
    // Exactly one pass: &amp;amp; becomes &amp;, never a raw ampersand.
    return value.replace(/&(?:amp|quot|apos|lt|gt);|&#(?:\d+|x[\da-f]+);/gi, entity => {
      const token = entity.slice(1, -1).toLowerCase();
      if (token[0] !== '#') return named[token];
      const point = token[1] === 'x' ? parseInt(token.slice(2), 16) : parseInt(token.slice(1), 10);
      return point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : entity;
    });
  }

  function kaching(properties) {
    const raw = properties && properties.__kaching_bundles;
    if (!raw) return null;
    try {
      let value = raw;
      if (typeof raw === 'string') {
        try { value = JSON.parse(raw); }
        catch (_) {
          // Some Liquid contexts escape a property's JSON string a second time.
          // Decode only a failed Kaching JSON value; ordinary properties stay exact.
          const entities = { quot: '"', amp: '&', apos: "'", lt: '<', gt: '>' };
          value = JSON.parse(raw.replace(/&(quot|amp|apos|lt|gt);|&#(\d+);|&#x([\da-f]+);/gi,
            (entity, named, decimal, hex) => named ? entities[named.toLowerCase()]
              : String.fromCodePoint(parseInt(decimal || hex, decimal ? 10 : 16))));
        }
      }
      return value && typeof value === 'object' && !Array.isArray(value) ? value : null;
    } catch (_) { return null; }
  }

  function normalizeLine(raw) {
    const variant = Number(raw.variant_id || raw.id);
    const quantity = Number(raw.quantity);
    if (!raw.key || !Number.isSafeInteger(variant) || variant < 1 || !Number.isSafeInteger(quantity) || quantity < 1) {
      throw new Error('Invalid cart line');
    }
    let properties = propertiesObject(raw.properties);
    if (raw.properties_are_liquid_escaped === true) {
      // Shopify Liquid property Drops add a layer of entity escaping. Only rows
      // explicitly marked by our Liquid snippet are decoded. Ajax data is exact.
      properties = Object.fromEntries(Object.entries(properties).map(([key, value]) =>
        [decodeLiquidProperty(key), decodeLiquidProperty(value)]));
    }
    return {
      key: String(raw.key),
      variant_id: variant,
      product_id: Number(raw.product_id),
      quantity,
      properties,
      selling_plan_id: Number(raw.selling_plan_id || raw.selling_plan_allocation?.selling_plan?.id) || null,
      final_line_price: Number(raw.final_line_price) || 0,
      original_line_price: Number(raw.original_line_price) || 0,
      parent: raw.parent_relationship || raw.parent_id || null,
      title: raw.product_title || raw.title || '',
      raw
    };
  }

  function canonicalProperties(line) {
    const properties = { ...line.properties };
    const metadata = kaching(properties);
    if (metadata) properties.__kaching_bundles = metadata;
    return properties;
  }

  function memberKey(line) {
    return stable([line.variant_id, line.selling_plan_id, canonicalProperties(line), line.parent]);
  }

  function identify(line, config) {
    const metadata = kaching(line.properties);
    if (line.parent) return { id: 'unsupported:' + line.key, supported: false, metadata };
    if (!Object.prototype.hasOwnProperty.call(line.properties, '__kaching_bundles')) {
      return { id: 'line:' + line.key, supported: true, role: 'paid', offer: { paidQuantity: 1, giftQuantity: 0 } };
    }
    if (!metadata || metadata.main !== true || metadata.gift || metadata.upsell || metadata.bundleProduct || metadata.collectionBreaksProduct || metadata.checkboxUpsell || metadata.personalisation) {
      return { id: 'unsupported:' + line.key, supported: false, metadata };
    }
    const offer = (config.offers || []).find(candidate =>
      String(candidate.productId || config.productId) === String(line.product_id) &&
      (candidate.variantId == null || Number(candidate.variantId) === line.variant_id) &&
      (candidate.sellingPlanId == null || line.selling_plan_id === null || Number(candidate.sellingPlanId) === line.selling_plan_id) &&
      String(candidate.deal) === String(metadata.deal) &&
      String(candidate.version) === String(metadata.v) &&
      String(candidate.bar) === String(metadata.bar || metadata.bid)
    );
    if (!offer || !Number.isSafeInteger(offer.paidQuantity) || offer.paidQuantity < 1 || !Number.isSafeInteger(offer.giftQuantity) || offer.giftQuantity < 0) {
      return { id: 'unsupported:' + line.key, supported: false, metadata };
    }
    const gift = metadata.bxgy === true;
    const validShape = offer.giftQuantity > 0
      ? !!metadata.id && !!metadata.bar && !metadata.bid && (gift ? metadata.pp == null : String(metadata.pp) === String(line.product_id))
      : !!metadata.bid && !metadata.id && !metadata.bar && !metadata.bxgy && String(metadata.pp) === String(line.product_id);
    if (!validShape) return { id: 'unsupported:' + line.key, supported: false, metadata };

    // These three fields describe the paid/free role, not a different offer.
    // Every other app property, offer version, market and plan remains in identity.
    const identity = { ...metadata };
    delete identity.pp;
    delete identity.main;
    delete identity.bxgy;
    const properties = { ...line.properties, __kaching_bundles: identity };
    return {
      id: 'offer:' + stable([line.variant_id, line.selling_plan_id, properties]),
      supported: true,
      offer,
      metadata,
      role: gift ? 'gift' : 'paid'
    };
  }

  function groupLines(rawLines, config = { offers: [] }) {
    const groups = new Map();
    rawLines.forEach(raw => {
      const line = normalizeLine(raw);
      const identity = identify(line, config);
      if (!groups.has(identity.id)) {
        groups.set(identity.id, {
          id: identity.id, lines: [], members: [], quantity: 0,
          final_line_price: 0, original_line_price: 0, supported: identity.supported,
          offer: identity.offer, bundle: !!Object.prototype.hasOwnProperty.call(line.properties, '__kaching_bundles')
        });
      }
      const group = groups.get(identity.id);
      line.role = identity.role;
      group.lines.push(line);
      group.quantity += line.quantity;
      group.final_line_price += line.final_line_price;
      group.original_line_price += line.original_line_price;
      const id = memberKey(line);
      let member = group.members.find(candidate => candidate.id === id);
      if (!member) {
        member = { id, role: identity.role, quantity: 0, lines: [] };
        group.members.push(member);
      }
      member.quantity += line.quantity;
      member.lines.push(line);
    });
    return Array.from(groups.values()).map(group => {
      const paid = group.members.filter(member => member.role === 'paid');
      const gifts = group.members.filter(member => member.role === 'gift');
      const paidQuantity = paid.reduce((sum, member) => sum + member.quantity, 0);
      const giftQuantity = gifts.reduce((sum, member) => sum + member.quantity, 0);
      const sets = group.supported ? paidQuantity / group.offer.paidQuantity : 0;
      group.valid = group.supported && paid.length === 1 && gifts.length === (group.offer.giftQuantity ? 1 : 0) &&
        Number.isSafeInteger(sets) && sets >= 1 && giftQuantity === sets * group.offer.giftQuantity;
      group.sets = group.valid ? sets : 0;
      group.step = group.supported ? group.offer.paidQuantity + group.offer.giftQuantity : 0;
      return group;
    });
  }

  function quantitiesSignature(group) {
    return stable(group.members.map(member => [member.id, member.quantity]).sort((a, b) => a[0].localeCompare(b[0])));
  }

  function mutationFor(group, action) {
    if (!['increase', 'decrease', 'remove'].includes(action)) throw new Error('Invalid action');
    if (!group.supported || (action !== 'remove' && !group.valid)) throw new Error('Unsafe offer mutation');
    if (action === 'increase') {
      // Both Kaching apps explicitly bypass this marker. Exact original metadata
      // and plan are retained, so another bundle or subscription is never applied.
      return {
        endpoint: 'cart/add.js?kaching_bundles=true',
        body: { items: group.members.map(member => {
          const line = member.lines[0];
          const item = {
            id: line.variant_id,
            quantity: member.role === 'gift' ? group.offer.giftQuantity : group.offer.paidQuantity,
            properties: { ...line.properties }
          };
          if (line.selling_plan_id) item.selling_plan = line.selling_plan_id;
          return item;
        }) }
      };
    }
    const updates = {};
    group.members.forEach(member => {
      const delta = member.role === 'gift' ? group.offer.giftQuantity : group.offer.paidQuantity;
      let remaining = action === 'remove' ? 0 : member.quantity - delta;
      member.lines.forEach(line => {
        const keep = Math.max(0, Math.min(line.quantity, remaining));
        updates[line.key] = keep;
        remaining -= keep;
      });
    });
    return { endpoint: 'cart/update.js', body: { updates } };
  }

  function cartQuantities(rawLines) {
    const quantities = new Map();
    rawLines.forEach(raw => {
      const line = normalizeLine(raw);
      const id = memberKey(line);
      quantities.set(id, (quantities.get(id) || 0) + line.quantity);
    });
    return quantities;
  }

  function mapSignature(quantities) {
    return stable(Array.from(quantities).filter(([, quantity]) => quantity > 0).sort((a, b) => a[0].localeCompare(b[0])));
  }

  function expectedQuantities(rawLines, group, action) {
    const quantities = cartQuantities(rawLines);
    group.members.forEach(member => {
      const amount = member.role === 'gift' ? group.offer.giftQuantity : group.offer.paidQuantity;
      const delta = action === 'remove' ? -member.quantity : action === 'increase' ? amount : -amount;
      quantities.set(member.id, (quantities.get(member.id) || 0) + delta);
    });
    return mapSignature(quantities);
  }

  function lineSignature(rawLines) {
    return stable(rawLines.map(raw => {
      const line = normalizeLine(raw);
      return [line.key, memberKey(line), line.quantity, line.final_line_price, line.original_line_price];
    }).sort((a, b) => a[0].localeCompare(b[0])));
  }

  function hasSections(sections) {
    return !!sections && SECTIONS.every(name => typeof sections[name] === 'string' && sections[name].trim());
  }

  async function transact(shown, action, config, io) {
    let failure = '';
    let response = null;
    let expected = null;
    try {
      const cart = await io.readCart();
      const fresh = groupLines(cart.items, config).find(group => group.id === shown.id);
      if (!fresh || quantitiesSignature(fresh) !== quantitiesSignature(shown)) failure = TEXT.changed;
      else {
        const change = mutationFor(fresh, action);
        expected = expectedQuantities(cart.items, fresh, action);
        response = await io.send(change);
      }
    } catch (error) {
      failure = error.status === 422 ? TEXT.stock : TEXT.failed;
    }
    try {
      // A failed request may already have executed. Never send it a second time.
      const cart = await io.refresh(response);
      if (expected && mapSignature(cartQuantities(cart.items)) !== expected) failure = failure || TEXT.changed;
      return { cart, failure, unsafe: false };
    } catch (error) {
      return { failure: TEXT.unknown, unsafe: true, error };
    }
  }

  function focusDrawer(current, activeElement, trap, preferred) {
    if (!current?.classList.contains('active') || typeof trap !== 'function') return false;
    const container = current.querySelector(current.classList.contains('is-empty') ? '.drawer__inner-empty' : '.drawer__inner');
    if (!container) return false;
    const usable = node => node && container.contains(node) && !node.disabled;
    const target = usable(preferred) ? preferred : usable(activeElement) ? activeElement : container.querySelector('.drawer__close') || container;
    // The native trap closes over first/last focusable elements. Rebuild it after
    // section replacement and after controls are unlocked, using the new nodes.
    trap(container, target);
    return true;
  }

  function nativeAddComplete(pending, loading) {
    if (loading) pending.loading = true;
    return !loading && (pending.loading || pending.nativeComplete || pending.rendered);
  }

  function sectionRequestURL(root, pageHref) {
    const page = new URL(pageHref);
    const url = new URL(root, page.origin);
    ['preview_theme_id', '_fd', 'pb'].forEach(name => {
      if (page.searchParams.has(name)) url.searchParams.set(name, page.searchParams.get(name));
    });
    url.searchParams.set('sections', SECTIONS.join(','));
    return url.href;
  }

  function trackDrawerOpening(initialActive, onOpen) {
    let previousActive = initialActive;
    return active => {
      const opened = active && !previousActive;
      previousActive = active;
      if (opened) onOpen();
    };
  }

  function mergeOfferConfig(base, verified) {
    const offers = new Map((base.offers || []).map(offer =>
      [stable([offer.variantId || base.variantId, offer.deal, offer.version, offer.bar]), { ...offer }]));
    if (Array.isArray(verified) && verified.length <= 36) {
      for (const offer of verified) {
        const shape = (base.offers || []).find(known => known.deal === offer?.deal &&
          known.paidQuantity === offer.paidQuantity && known.giftQuantity === offer.giftQuantity &&
          known.sellingPlanId === offer.sellingPlanId);
        if (!shape || offer.productId !== base.productId || offer.variantId !== base.variantId ||
            typeof offer.version !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(offer.version) ||
            !/^[A-Za-z0-9_-]{1,64}$/.test(offer.bar || '')) continue;
        const key = stable([offer.variantId, offer.deal, offer.version, offer.bar]);
        if (!offers.has(key)) offers.set(key, {
          productId: offer.productId, variantId: offer.variantId, deal: offer.deal, version: offer.version,
          bar: offer.bar, paidQuantity: offer.paidQuantity, giftQuantity: offer.giftQuantity, sellingPlanId: offer.sellingPlanId
        });
      }
    }
    return { ...base, offers: Array.from(offers.values()) };
  }

  function init(win) {
    const doc = win.document;
    const state = {
      busy: false, confirming: false, unsafe: false, integrity: false,
      groups: new Map(), error: '', disabled: new Map(), scheduled: false,
      pendingAdds: new Map(), confirmationScheduled: false
    };
    const drawer = () => doc.querySelector('cart-drawer');
    const route = path => (win.Shopify?.routes?.root || '/').replace(/\/?$/, '/') + path;
    const isBusy = () => state.busy || state.confirming || state.confirmationScheduled || state.pendingAdds.size > 0;
    const nativeLoading = () => !!doc.querySelector('product-form [type="submit"].loading');
    const config = () => {
      const node = drawer()?.querySelector('[data-revilia-cart-config]');
      const base = node ? JSON.parse(node.textContent) : { offers: [] };
      let verified = [];
      try { verified = win.ReviliaOffers?.getOffers() || []; } catch (_) {}
      return mergeOfferConfig(base, verified);
    };
    const money = cents => new Intl.NumberFormat('fr-FR', {
      style: 'currency', currency: drawer()?.querySelector('#CartDrawer')?.dataset.reviliaCurrency || 'EUR'
    }).format(cents / 100);
    const bindDrawerFocus = preferred => focusDrawer(drawer(), doc.activeElement,
      typeof trapFocus === 'function' ? trapFocus : null, preferred);

    function showStatus() {
      const current = drawer();
      if (!current) return;
      const container = current.classList.contains('is-empty')
        ? current.querySelector('.drawer__inner-empty')
        : current.querySelector('.drawer__footer');
      if (!container) return;
      let node = container.querySelector('[data-revilia-cart-error]');
      if (!node) {
        node = doc.createElement('p');
        node.dataset.reviliaCartError = '';
        node.className = 'revilia-cart-error';
        node.setAttribute('role', 'alert');
        container.prepend(node);
      }
      const text = state.error || (state.integrity ? TEXT.invalid : '');
      if (node.textContent !== text) node.textContent = text;
      node.hidden = !text;
      let recheck = container.querySelector('[data-revilia-cart-recheck]');
      if (!recheck) {
        recheck = doc.createElement('button');
        recheck.type = 'button';
        recheck.className = 'revilia-cart-recheck';
        recheck.dataset.reviliaCartRecheck = '';
        recheck.textContent = 'Vérifier le panier';
        node.after(recheck);
      }
      recheck.hidden = !state.unsafe;
      recheck.disabled = isBusy() || nativeLoading();
    }

    function lockControls() {
      const current = drawer();
      if (!current) return;
      const busy = isBusy() || nativeLoading();
      current.setAttribute('aria-busy', String(busy));
      current.querySelectorAll('[data-revilia-cart-action], #CartDrawer-Checkout').forEach(control => {
        const checkout = control.id === 'CartDrawer-Checkout';
        const lock = busy || state.unsafe || (checkout && state.integrity);
        if (lock) {
          if (!state.disabled.has(control)) state.disabled.set(control, control.disabled);
          control.disabled = true;
        } else if (state.disabled.has(control)) {
          control.disabled = state.disabled.get(control);
          state.disabled.delete(control);
        }
      });
      for (const control of state.disabled.keys()) if (!control.isConnected) state.disabled.delete(control);
      const status = current.querySelector('#CartDrawer-LiveRegionText');
      const statusText = busy ? TEXT.pending : '';
      if (status && status.textContent !== statusText) status.textContent = statusText;
      showStatus();
    }

    function actionButton(label, action, group) {
      const button = doc.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.dataset.reviliaCartAction = action;
      button.dataset.reviliaGroup = group.id;
      button.disabled = !group.supported || (action !== 'remove' && !group.valid);
      button.setAttribute('aria-label', action === 'remove'
        ? 'Supprimer cette offre du panier'
        : (action === 'increase' ? 'Augmenter' : 'Diminuer') + ' la quantité de ' + (group.step || 1) + ((group.step || 1) === 1 ? ' unité' : ' unités'));
      return button;
    }

    function hydrate() {
      const current = drawer();
      if (!current) return;
      const rows = Array.from(current.querySelectorAll('[data-revilia-line]'));
      if (rows.length && rows.every(row => row.dataset.reviliaEnhanced === 'true')) {
        lockControls();
        return;
      }
      try {
        const groups = groupLines(rows.map(row => JSON.parse(row.dataset.reviliaLine)), config());
        const byKey = new Map(rows.map(row => [JSON.parse(row.dataset.reviliaLine).key, row]));
        state.groups = new Map(groups.map(group => [group.id, group]));
        state.integrity = groups.some(group => group.supported && !group.valid);
        groups.forEach(group => {
          const first = group.lines.find(line => line.role === 'paid') || group.lines[0];
          const primary = byKey.get(first.key);
          group.lines.forEach(line => {
            const row = byKey.get(line.key);
            row.dataset.reviliaEnhanced = 'true';
            row.hidden = row !== primary;
            row.querySelector('.revilia-cart-fallback').hidden = true;
          });
          const container = primary.querySelector('[data-revilia-controls]');
          container.replaceChildren();
          const controls = doc.createElement('div');
          controls.className = 'revilia-cart-controls';
          const quantity = doc.createElement('div');
          quantity.className = 'revilia-cart-quantity';
          const output = doc.createElement('output');
          output.textContent = String(group.quantity);
          output.setAttribute('aria-label', 'Quantité totale d’unités');
          quantity.append(actionButton('−', 'decrease', group), output, actionButton('+', 'increase', group));
          const remove = actionButton('Supprimer', 'remove', group);
          remove.className = 'revilia-cart-remove';
          controls.append(quantity, remove);
          const price = doc.createElement('p');
          price.className = 'revilia-cart-unit-price';
          price.textContent = money(group.final_line_price / group.quantity) + ' par unité';
          container.append(controls, price);
          if (!group.valid) {
            const notice = doc.createElement('p');
            notice.className = 'revilia-cart-item-notice';
            notice.textContent = group.supported ? TEXT.invalid : TEXT.unsupported;
            container.append(notice);
          }
        });
      } catch (error) {
        state.unsafe = true;
        state.error = TEXT.unknown;
        win.console.error('[Revilia cart] Invalid cart presentation', error);
      }
      lockControls();
    }

    async function request(url, body) {
      const response = await win.fetch(url, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      const result = await response.json();
      if (!response.ok || result.status >= 400) {
        const error = new Error('Cart request failed');
        error.status = response.status;
        throw error;
      }
      return result;
    }

    function sectionsMatch(sections, cart) {
      if (!hasSections(sections)) return false;
      try {
        const parsed = new win.DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const content = parsed.querySelector('#CartDrawer');
        const rows = Array.from(parsed.querySelectorAll('[data-revilia-line]')).map(row => JSON.parse(row.dataset.reviliaLine));
        return !!content && Number(content.dataset.reviliaTotal) === cart.total_price &&
          Number(content.dataset.reviliaOriginalTotal) === cart.original_total_price &&
          lineSignature(rows) === lineSignature(cart.items);
      } catch (_) { return false; }
    }

    async function refresh(mutationResponse) {
      let cart = await request(route('cart.js'));
      let sections = mutationResponse?.sections;
      if (!sectionsMatch(sections, cart)) {
        // /cart with Accept: application/json returns cart JSON even when sections
        // are requested. The localized storefront root returns the section map.
        sections = await request(sectionRequestURL(route(''), win.location.href));
        // A cart mutation in another tab must not pair stale HTML with a new total.
        cart = await request(route('cart.js'));
      }
      if (!sectionsMatch(sections, cart)) throw new Error('Cart sections do not match confirmed cart');
      const current = drawer();
      if (!current || typeof current.renderContents !== 'function') throw new Error('Cart renderer unavailable');
      current.classList.toggle('is-empty', cart.item_count === 0);
      current.renderContents(Object.assign({}, cart, { sections }), true);
      // Shrine replaces only #CartDrawer.innerHTML; its outer data attributes
      // otherwise retain the totals from the first page render.
      const content = current.querySelector('#CartDrawer');
      content.dataset.reviliaTotal = String(cart.total_price);
      content.dataset.reviliaOriginalTotal = String(cart.original_total_price);
      if (typeof cart.currency === 'string') content.dataset.reviliaCurrency = cart.currency;
      state.unsafe = false;
      hydrate();
      bindDrawerFocus();
      return cart;
    }

    async function mutate(groupId, action) {
      if (isBusy() || nativeLoading() || state.unsafe) return;
      const shown = state.groups.get(groupId);
      if (!shown || !shown.supported || (action !== 'remove' && !shown.valid)) return;
      state.busy = true;
      state.error = '';
      lockControls();
      const outcome = await transact(shown, action, config(), {
        readCart: () => request(route('cart.js')),
        send: change => request(route(change.endpoint), Object.assign({}, change.body, {
            sections: SECTIONS, sections_url: win.location.pathname
          })),
        refresh
      });
      state.unsafe = outcome.unsafe;
      state.error = outcome.failure;
      if (outcome.error) win.console.error('[Revilia cart] Confirmation failed', outcome.error);
      state.busy = false;
      lockControls();
      const matching = Array.from(drawer()?.querySelectorAll('[data-revilia-cart-action]') || [])
        .find(control => control.dataset.reviliaGroup === groupId && control.dataset.reviliaCartAction === action && !control.disabled);
      bindDrawerFocus(matching);
    }

    async function confirmExternal() {
      if (state.busy || state.confirming || state.pendingAdds.size || nativeLoading()) return;
      state.confirming = true;
      lockControls();
      try {
        await refresh(null);
        state.error = '';
      } catch (error) {
        state.unsafe = true;
        state.error = TEXT.unknown;
        win.console.error('[Revilia cart] External confirmation failed', error);
      }
      state.confirming = false;
      lockControls();
      bindDrawerFocus();
    }

    function scheduleConfirmation() {
      if (state.confirmationScheduled) return;
      state.confirmationScheduled = true;
      // Lock immediately, including the frame before a queued open/back-navigation
      // confirmation starts. Existing content stays in place until fresh sections arrive.
      lockControls();
      win.queueMicrotask(() => {
        state.confirmationScheduled = false;
        confirmExternal();
      });
    }

    function watchNativeAdd(form) {
      const submit = form.querySelector('[type="submit"]');
      if (!submit || state.pendingAdds.has(form)) return;
      const pending = {
        loading: false, nativeComplete: false, rendered: false,
        inner: drawer()?.querySelector('.drawer__inner'), observer: null, timer: null, check: null
      };
      state.pendingAdds.set(form, pending);
      const check = () => {
        if (nativeAddComplete(pending, submit.classList.contains('loading'))) {
          pending.observer.disconnect();
          win.clearTimeout(pending.timer);
          state.pendingAdds.delete(form);
          scheduleConfirmation();
        }
        lockControls();
      };
      pending.check = check;
      pending.observer = new win.MutationObserver(check);
      pending.observer.observe(submit, { attributes: true, attributeFilter: ['class'] });
      pending.timer = win.setTimeout(() => {
        if (state.pendingAdds.has(form)) {
          // Keep observing: a slow request can still succeed after this timeout.
          // No read or elapsed timer is treated as proof that mutation completed.
          state.unsafe = true;
          state.error = TEXT.timeout;
          lockControls();
        }
      }, 45000);
      // Kaching can delay the native handler before it adds its loading class.
      // A first microtask with no spinner is not a completion or failure signal.
      win.queueMicrotask(check);
      lockControls();
    }

    doc.addEventListener('click', event => {
      const directAdd = event.target.closest?.('[id^="SectionAtcBtn-"]');
      if (directAdd) {
        // Section CTAs call ProductForm.handleSubmit directly instead of submitting
        // a form. Apply the same lock and completion observation to this path.
        const form = doc.querySelector('.main-product-form form');
        if (isBusy() || nativeLoading() || state.unsafe) {
          event.preventDefault();
          event.stopImmediatePropagation();
        } else if (form) watchNativeAdd(form);
        return;
      }
      const target = event.target.closest?.('[data-revilia-cart-action]');
      if (target?.closest('cart-drawer')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        mutate(target.dataset.reviliaGroup, target.dataset.reviliaCartAction);
      } else if (event.target.closest?.('[data-revilia-cart-recheck]')) {
        event.preventDefault();
        confirmExternal();
      } else if ((isBusy() || nativeLoading() || state.unsafe || state.integrity) &&
          event.target.closest?.('cart-drawer [name="checkout"], cart-drawer [href*="/checkout"]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    doc.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof win.HTMLFormElement)) return;
      const inDrawer = !!form.closest('cart-drawer');
      const isAdd = /\/cart\/add(?:\.js)?(?:$|\?)/.test(form.action);
      if ((inDrawer || isAdd) && (isBusy() || nativeLoading() || state.unsafe || (inDrawer && state.integrity))) {
        event.preventDefault();
        event.stopImmediatePropagation();
      } else if (isAdd) watchNativeAdd(form);
    }, true);

    function start() {
      hydrate();
      const current = drawer();
      if (!current) return;
      // This private presentation event contains no cart mutation or app payload.
      doc.addEventListener('revilia:offers-verified', () => {
        current.querySelectorAll('[data-revilia-line]').forEach(row => row.removeAttribute('data-revilia-enhanced'));
        hydrate();
      });
      new win.MutationObserver(() => {
        const inner = current.querySelector('.drawer__inner');
        state.pendingAdds.forEach(pending => {
          if (inner !== pending.inner) {
            pending.rendered = true;
            pending.check();
          }
        });
        if (state.scheduled) return;
        state.scheduled = true;
        win.queueMicrotask(() => {
          state.scheduled = false;
          hydrate();
        });
      }).observe(current, { childList: true, subtree: true });
      // Opening must establish focus even when reduced motion prevents a native
      // transitionend event. This observes only the outer drawer's class.
      const trackOpening = trackDrawerOpening(current.classList.contains('active'), scheduleConfirmation);
      new win.MutationObserver(() => {
        trackOpening(current.classList.contains('active'));
        bindDrawerFocus();
      }).observe(current, {
        attributes: true, attributeFilter: ['class']
      });
      // Checkout back-navigation can restore an old drawer and header from bfcache
      // without changing any classes. Reconfirm even if the drawer stayed closed.
      win.addEventListener('pageshow', event => {
        if (event.persisted) scheduleConfirmation();
      });
      bindDrawerFocus();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source === 'product-form') {
            state.pendingAdds.forEach(pending => {
              pending.nativeComplete = true;
              win.queueMicrotask(pending.check);
            });
            scheduleConfirmation();
          }
        });
      }
    }
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }

  return { stable, propertiesObject, decodeLiquidProperty, kaching, normalizeLine, memberKey, identify, groupLines, quantitiesSignature,
    mutationFor, cartQuantities, mapSignature, expectedQuantities, lineSignature, hasSections, transact, focusDrawer,
    nativeAddComplete, sectionRequestURL, trackDrawerOpening, mergeOfferConfig, init };
});
